/**
 * Mass-lock (plan R6; spec §4.2; D-TBD 19). The drive is blocked within `stationM` of a station's hull (the bounding
 * sphere: R + `stationM` from its centre, scale build SC-5a2a), `relayM` of a relay, `shipM` of a large ship, inside a body's (or the star's) shell, or while a hostile holds a weapon lock.
 *
 * `buildMassLockInput` runs once per system (it allocates); `massLockOf`, `nearestSurface` and `setMassLockShip`
 * run per tick and allocate nothing (D-014). Positions are float64 system-frame metres.
 */
import type { SystemLayout } from '../../galaxy/layout/types'
import type { MassLockReason } from '../../reach/runtime/types'
import { siteRadius } from '../../reach/session/siteRadius'
import { loadTravelConfig, type TravelConfig } from '../config'

/** The shell index written for the star (bodies use their layout index). */
export const STAR_SHELL_INDEX = -1

export interface MassLockInput {
  /** Station centres, xyz interleaved, in `layout.stations` order. */
  readonly stations: Float64Array
  readonly stationCount: number
  /**
   * Station gameplay radii R (metres; `siteRadius`, the bounding sphere round the model origin), parallel to
   * `stations`. The station lock reaches R + `stationM` from the centre, i.e. `stationM` off the hull.
   */
  readonly stationRadii: Float64Array
  /** Relay centres, xyz interleaved, in `layout.relays` order. */
  readonly relays: Float64Array
  readonly relayCount: number
  /** Shell centres (bodies in `layout.bodies` order, then the star), xyz interleaved. */
  readonly shells: Float64Array
  /** Shell radii, parallel to `shells`. */
  readonly shellRadii: Float64Array
  /** The layout index of each shell (`STAR_SHELL_INDEX` for the star). */
  readonly shellIndex: Int32Array
  readonly shellCount: number
  /** Large-ship centres, xyz interleaved; the runtime refreshes them per tick with `setMassLockShip`. */
  readonly ships: Float64Array
  /** Each ship slot's caller-side id (for example its eid), written into `out.index` when it locks. */
  readonly shipIds: Int32Array
  /** Live ship slots (0..shipCapacity). */
  shipCount: number
  readonly shipCapacity: number
}

export interface MassLockHit {
  /** The offending object's index (layout index, `STAR_SHELL_INDEX`, a ship id, or −1 for none or a weapon lock). */
  index: number
}

export function createMassLockHit(): MassLockHit {
  return { index: -1 }
}

/**
 * Typed arrays of the system's stations, relays and shells (bodies and the star), plus `capacity` large-ship slots.
 * Built once per system. `cfg` (for the star shell) defaults to reach-travel.json.
 */
export function buildMassLockInput(
  layout: SystemLayout,
  capacity: number,
  cfg: TravelConfig = loadTravelConfig(),
): MassLockInput {
  const ns = layout.stations.length
  const nr = layout.relays.length
  const nb = layout.bodies.length
  const stations = new Float64Array(ns * 3)
  const stationRadii = new Float64Array(ns)
  for (let i = 0; i < ns; i++) {
    writeVec(stations, i, layout.stations[i].position)
    stationRadii[i] = siteRadius(layout.stations[i])
  }
  const relays = new Float64Array(nr * 3)
  for (let i = 0; i < nr; i++) writeVec(relays, i, layout.relays[i].position)
  const shells = new Float64Array((nb + 1) * 3)
  const shellRadii = new Float64Array(nb + 1)
  const shellIndex = new Int32Array(nb + 1)
  for (let i = 0; i < nb; i++) {
    const b = layout.bodies[i]
    writeVec(shells, i, b.position)
    shellRadii[i] = b.shellM
    shellIndex[i] = b.index
  }
  // The star sits at the system origin (plan §2.2).
  shellRadii[nb] = layout.star.radiusM * cfg.massLock.starShellFactor + cfg.massLock.starShellExtraM
  shellIndex[nb] = STAR_SHELL_INDEX
  const cap = Math.max(0, Math.floor(capacity))
  return {
    stations,
    stationCount: ns,
    stationRadii,
    relays,
    relayCount: nr,
    shells,
    shellRadii,
    shellIndex,
    shellCount: nb + 1,
    ships: new Float64Array(cap * 3),
    shipIds: new Int32Array(cap),
    shipCount: 0,
    shipCapacity: cap,
  }
}

function writeVec(arr: Float64Array, i: number, v: readonly [number, number, number]): void {
  arr[i * 3] = v[0]
  arr[i * 3 + 1] = v[1]
  arr[i * 3 + 2] = v[2]
}

/** Writes large-ship slot `slot` (allocation-free); slots at or beyond `shipCount` are ignored by `massLockOf`. */
export function setMassLockShip(input: MassLockInput, slot: number, id: number, x: number, y: number, z: number): void {
  if (slot < 0 || slot >= input.shipCapacity) return
  input.ships[slot * 3] = x
  input.ships[slot * 3 + 1] = y
  input.ships[slot * 3 + 2] = z
  input.shipIds[slot] = id
}

/** The index of the first point in `arr` (count points) within `radius` of p, or −1. */
function firstWithin(arr: Float64Array, count: number, px: number, py: number, pz: number, radius: number): number {
  const r2 = radius * radius
  for (let i = 0; i < count; i++) {
    const dx = arr[i * 3] - px
    const dy = arr[i * 3 + 1] - py
    const dz = arr[i * 3 + 2] - pz
    if (dx * dx + dy * dy + dz * dz < r2) return i
  }
  return -1
}

/** The index of the first point in `arr` (count points) within `radii[i] + extra` of p, or −1. */
function firstWithinHull(
  arr: Float64Array,
  radii: Float64Array,
  count: number,
  px: number,
  py: number,
  pz: number,
  extra: number,
): number {
  for (let i = 0; i < count; i++) {
    const dx = arr[i * 3] - px
    const dy = arr[i * 3 + 1] - py
    const dz = arr[i * 3 + 2] - pz
    const r = radii[i] + extra
    if (dx * dx + dy * dy + dz * dz < r * r) return i
  }
  return -1
}

/**
 * Why the drive is mass-locked at p, checked in `MassLockReason` order (station, relay, body, ship, weapon lock);
 * 'none' when it is free. Writes the offending object's index into `out` (−1 for none or a weapon lock).
 */
export function massLockOf(
  px: number,
  py: number,
  pz: number,
  input: MassLockInput,
  hostileLock: boolean,
  cfg: TravelConfig,
  out: MassLockHit,
): MassLockReason {
  const m = cfg.massLock
  let i = firstWithinHull(input.stations, input.stationRadii, input.stationCount, px, py, pz, m.stationM)
  if (i >= 0) {
    out.index = i
    return 'station'
  }
  i = firstWithin(input.relays, input.relayCount, px, py, pz, m.relayM)
  if (i >= 0) {
    out.index = i
    return 'relay'
  }
  for (let s = 0; s < input.shellCount; s++) {
    const dx = input.shells[s * 3] - px
    const dy = input.shells[s * 3 + 1] - py
    const dz = input.shells[s * 3 + 2] - pz
    const r = input.shellRadii[s]
    if (dx * dx + dy * dy + dz * dz < r * r) {
      out.index = input.shellIndex[s]
      return 'body'
    }
  }
  const n = input.shipCount < input.shipCapacity ? input.shipCount : input.shipCapacity
  i = firstWithin(input.ships, n, px, py, pz, m.shipM)
  if (i >= 0) {
    out.index = input.shipIds[i]
    return 'ship'
  }
  out.index = -1
  if (hostileLock && m.weaponLock) return 'weaponLock'
  return 'none'
}

/** The distance from p to the nearest shell surface (bodies and the star); 0 inside a shell, Infinity with none. */
export function nearestSurface(px: number, py: number, pz: number, input: MassLockInput): number {
  let best = Infinity
  for (let s = 0; s < input.shellCount; s++) {
    const dx = input.shells[s * 3] - px
    const dy = input.shells[s * 3 + 1] - py
    const dz = input.shells[s * 3 + 2] - pz
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz) - input.shellRadii[s]
    if (d < best) best = d
  }
  return best < 0 ? 0 : best
}
